/**
 * Object Bank - Comprehensive emoji database for Letter Mode
 * Each object has a letter, emoji, and name for matching gameplay
 */

export interface ObjectBankEntry {
  letter: string;
  emoji: string;
  name: string;
}

/**
 * Complete object bank with minimum 5 objects per letter (A-Z)
 * Based on UX Design Spec Appendix B
 */
export const OBJECT_BANK: ObjectBankEntry[] = [
  // A
  { letter: 'A', emoji: '🍎', name: 'Apple' },
  { letter: 'A', emoji: '✈️', name: 'Airplane' },
  { letter: 'A', emoji: '🐊', name: 'Alligator' },
  { letter: 'A', emoji: '👽', name: 'Alien' },
  { letter: 'A', emoji: '🎨', name: 'Art' },
  { letter: 'A', emoji: '⚓', name: 'Anchor' },

  // B
  { letter: 'B', emoji: '🎈', name: 'Balloon' },
  { letter: 'B', emoji: '🐻', name: 'Bear' },
  { letter: 'B', emoji: '📚', name: 'Book' },
  { letter: 'B', emoji: '🦋', name: 'Butterfly' },
  { letter: 'B', emoji: '🏀', name: 'Ball' },
  { letter: 'B', emoji: '🚌', name: 'Bus' },
  { letter: 'B', emoji: '🍌', name: 'Banana' },
  { letter: 'B', emoji: '🐝', name: 'Bee' },

  // C
  { letter: 'C', emoji: '🐱', name: 'Cat' },
  { letter: 'C', emoji: '🚗', name: 'Car' },
  { letter: 'C', emoji: '🍰', name: 'Cake' },
  { letter: 'C', emoji: '🌵', name: 'Cactus' },
  { letter: 'C', emoji: '👑', name: 'Crown' },
  { letter: 'C', emoji: '🐄', name: 'Cow' },
  { letter: 'C', emoji: '🍪', name: 'Cookie' },

  // D
  { letter: 'D', emoji: '🐕', name: 'Dog' },
  { letter: 'D', emoji: '🦕', name: 'Dinosaur' },
  { letter: 'D', emoji: '🚪', name: 'Door' },
  { letter: 'D', emoji: '🥁', name: 'Drum' },
  { letter: 'D', emoji: '🦆', name: 'Duck' },
  { letter: 'D', emoji: '💎', name: 'Diamond' },
  { letter: 'D', emoji: '🍩', name: 'Donut' },

  // E
  { letter: 'E', emoji: '🥚', name: 'Egg' },
  { letter: 'E', emoji: '🐘', name: 'Elephant' },
  { letter: 'E', emoji: '👁️', name: 'Eye' },
  { letter: 'E', emoji: '🦅', name: 'Eagle' },
  { letter: 'E', emoji: '🌍', name: 'Earth' },
  { letter: 'E', emoji: '🍆', name: 'Eggplant' },

  // F
  { letter: 'F', emoji: '🦊', name: 'Fox' },
  { letter: 'F', emoji: '🐠', name: 'Fish' },
  { letter: 'F', emoji: '🌸', name: 'Flower' },
  { letter: 'F', emoji: '🍟', name: 'Fries' },
  { letter: 'F', emoji: '🔥', name: 'Fire' },
  { letter: 'F', emoji: '🏈', name: 'Football' },
  { letter: 'F', emoji: '🍴', name: 'Fork' },

  // G
  { letter: 'G', emoji: '🎸', name: 'Guitar' },
  { letter: 'G', emoji: '🦒', name: 'Giraffe' },
  { letter: 'G', emoji: '👻', name: 'Ghost' },
  { letter: 'G', emoji: '🍇', name: 'Grapes' },
  { letter: 'G', emoji: '🎁', name: 'Gift' },
  { letter: 'G', emoji: '🌏', name: 'Globe' },

  // H
  { letter: 'H', emoji: '🏠', name: 'House' },
  { letter: 'H', emoji: '🐴', name: 'Horse' },
  { letter: 'H', emoji: '🎩', name: 'Hat' },
  { letter: 'H', emoji: '🍯', name: 'Honey' },
  { letter: 'H', emoji: '❤️', name: 'Heart' },
  { letter: 'H', emoji: '🦔', name: 'Hedgehog' },
  { letter: 'H', emoji: '🚁', name: 'Helicopter' },

  // I
  { letter: 'I', emoji: '🍦', name: 'Ice Cream' },
  { letter: 'I', emoji: '🏝️', name: 'Island' },
  { letter: 'I', emoji: '💡', name: 'Idea' },

  // J
  { letter: 'J', emoji: '🤹', name: 'Juggle' },
  { letter: 'J', emoji: '👖', name: 'Jeans' },
  { letter: 'J', emoji: '🕹️', name: 'Joystick' },
  { letter: 'J', emoji: '🧃', name: 'Juice' },
  { letter: 'J', emoji: '💎', name: 'Jewel' },

  // K
  { letter: 'K', emoji: '🔑', name: 'Key' },
  { letter: 'K', emoji: '🦘', name: 'Kangaroo' },
  { letter: 'K', emoji: '👑', name: 'King' },
  { letter: 'K', emoji: '🪁', name: 'Kite' },
  { letter: 'K', emoji: '🥋', name: 'Karate' },
  { letter: 'K', emoji: '🔪', name: 'Knife' },

  // L
  { letter: 'L', emoji: '🦁', name: 'Lion' },
  { letter: 'L', emoji: '🍋', name: 'Lemon' },
  { letter: 'L', emoji: '💡', name: 'Light' },
  { letter: 'L', emoji: '🪜', name: 'Ladder' },
  { letter: 'L', emoji: '🦎', name: 'Lizard' },
  { letter: 'L', emoji: '🍃', name: 'Leaf' },
  { letter: 'L', emoji: '🔒', name: 'Lock' },

  // M
  { letter: 'M', emoji: '🐵', name: 'Monkey' },
  { letter: 'M', emoji: '🌙', name: 'Moon' },
  { letter: 'M', emoji: '🍄', name: 'Mushroom' },
  { letter: 'M', emoji: '🎵', name: 'Music' },
  { letter: 'M', emoji: '🔍', name: 'Magnifier' },
  { letter: 'M', emoji: '💌', name: 'Mail' },

  // N
  { letter: 'N', emoji: '🥜', name: 'Nut' },
  { letter: 'N', emoji: '📰', name: 'News' },
  { letter: 'N', emoji: '🪺', name: 'Nest' },
  { letter: 'N', emoji: '👃', name: 'Nose' },
  { letter: 'N', emoji: '🎵', name: 'Note' },
  { letter: 'N', emoji: '🌃', name: 'Night' },

  // O
  { letter: 'O', emoji: '🐙', name: 'Octopus' },
  { letter: 'O', emoji: '🍊', name: 'Orange' },
  { letter: 'O', emoji: '🦉', name: 'Owl' },
  { letter: 'O', emoji: '🌊', name: 'Ocean' },
  { letter: 'O', emoji: '🥥', name: 'Coconut' },

  // P
  { letter: 'P', emoji: '🐼', name: 'Panda' },
  { letter: 'P', emoji: '🍕', name: 'Pizza' },
  { letter: 'P', emoji: '🌱', name: 'Plant' },
  { letter: 'P', emoji: '✏️', name: 'Pencil' },
  { letter: 'P', emoji: '🎹', name: 'Piano' },
  { letter: 'P', emoji: '🍐', name: 'Pear' },
  { letter: 'P', emoji: '🥞', name: 'Pancake' },

  // Q
  { letter: 'Q', emoji: '👸', name: 'Queen' },
  { letter: 'Q', emoji: '❓', name: 'Question' },
  { letter: 'Q', emoji: '🪶', name: 'Quill' },

  // R
  { letter: 'R', emoji: '🚀', name: 'Rocket' },
  { letter: 'R', emoji: '🌈', name: 'Rainbow' },
  { letter: 'R', emoji: '🐰', name: 'Rabbit' },
  { letter: 'R', emoji: '🌹', name: 'Rose' },
  { letter: 'R', emoji: '🤖', name: 'Robot' },
  { letter: 'R', emoji: '🦏', name: 'Rhino' },

  // S
  { letter: 'S', emoji: '⭐', name: 'Star' },
  { letter: 'S', emoji: '🌞', name: 'Sun' },
  { letter: 'S', emoji: '🐍', name: 'Snake' },
  { letter: 'S', emoji: '🍓', name: 'Strawberry' },
  { letter: 'S', emoji: '🦈', name: 'Shark' },
  { letter: 'S', emoji: '🥗', name: 'Salad' },
  { letter: 'S', emoji: '👟', name: 'Shoe' },

  // T
  { letter: 'T', emoji: '🐯', name: 'Tiger' },
  { letter: 'T', emoji: '🌳', name: 'Tree' },
  { letter: 'T', emoji: '🚂', name: 'Train' },
  { letter: 'T', emoji: '🎺', name: 'Trumpet' },
  { letter: 'T', emoji: '🐢', name: 'Turtle' },
  { letter: 'T', emoji: '🚕', name: 'Taxi' },
  { letter: 'T', emoji: '🦃', name: 'Turkey' },

  // U
  { letter: 'U', emoji: '☂️', name: 'Umbrella' },
  { letter: 'U', emoji: '🦄', name: 'Unicorn' },
  { letter: 'U', emoji: '🆙', name: 'Up' },

  // V
  { letter: 'V', emoji: '🌋', name: 'Volcano' },
  { letter: 'V', emoji: '🎻', name: 'Violin' },
  { letter: 'V', emoji: '🚐', name: 'Van' },
  { letter: 'V', emoji: '🦅', name: 'Vulture' },
  { letter: 'V', emoji: '🥕', name: 'Vegetable' },

  // W
  { letter: 'W', emoji: '🐋', name: 'Whale' },
  { letter: 'W', emoji: '🍉', name: 'Watermelon' },
  { letter: 'W', emoji: '🪟', name: 'Window' },
  { letter: 'W', emoji: '🌊', name: 'Wave' },
  { letter: 'W', emoji: '🐺', name: 'Wolf' },
  { letter: 'W', emoji: '⌚', name: 'Watch' },

  // X
  { letter: 'X', emoji: '🎄', name: 'Xmas Tree' },
  { letter: 'X', emoji: '📦', name: 'Box' },
  { letter: 'X', emoji: '🦴', name: 'X-ray' },

  // Y
  { letter: 'Y', emoji: '🧶', name: 'Yarn' },
  { letter: 'Y', emoji: '☀️', name: 'Yellow' },
  { letter: 'Y', emoji: '🛥️', name: 'Yacht' },
  { letter: 'Y', emoji: '🥱', name: 'Yawn' },

  // Z
  { letter: 'Z', emoji: '🦓', name: 'Zebra' },
  { letter: 'Z', emoji: '0️⃣', name: 'Zero' },
  { letter: 'Z', emoji: '🤐', name: 'Zipper' },

  // Combinations/Digraphs
  // CH
  { letter: 'CH', emoji: '🪑', name: 'Chair' },
  { letter: 'CH', emoji: '🧀', name: 'Cheese' },
  { letter: 'CH', emoji: '🐔', name: 'Chicken' },
  { letter: 'CH', emoji: '🍫', name: 'Chocolate' },
  { letter: 'CH', emoji: '🍒', name: 'Cherry' },
  { letter: 'CH', emoji: '⛪', name: 'Church' },

  // SH
  { letter: 'SH', emoji: '🚢', name: 'Ship' },
  { letter: 'SH', emoji: '🐑', name: 'Sheep' },
  { letter: 'SH', emoji: '🦈', name: 'Shark' },
  { letter: 'SH', emoji: '👞', name: 'Shoe' },
  { letter: 'SH', emoji: '🐚', name: 'Shell' },
  { letter: 'SH', emoji: '🦐', name: 'Shrimp' },

  // TH
  { letter: 'TH', emoji: '🌡️', name: 'Thermometer' },
  { letter: 'TH', emoji: '👍', name: 'Thumb' },
  { letter: 'TH', emoji: '💭', name: 'Thought' },
  { letter: 'TH', emoji: '🧵', name: 'Thread' },
  { letter: 'TH', emoji: '🤔', name: 'Think' },
];

/**
 * Get all objects that start with a specific letter
 */
export function getObjectsForLetter(letter: string): ObjectBankEntry[] {
  const upperLetter = letter.toUpperCase();
  return OBJECT_BANK.filter(obj => obj.letter === upperLetter);
}

/**
 * Get all unique letters in the object bank
 */
export function getAllLetters(): string[] {
  const letters = new Set(OBJECT_BANK.map(obj => obj.letter));
  return Array.from(letters).sort();
}

/**
 * Get a random object for a specific letter
 */
export function getRandomObjectForLetter(letter: string): ObjectBankEntry | null {
  const objects = getObjectsForLetter(letter);
  if (objects.length === 0) return null;
  return objects[Math.floor(Math.random() * objects.length)];
}

/**
 * Get a random object that does NOT start with the specified letter
 */
export function getRandomObjectNotForLetter(letter: string): ObjectBankEntry {
  const upperLetter = letter.toUpperCase();
  const otherObjects = OBJECT_BANK.filter(obj => obj.letter !== upperLetter);
  return otherObjects[Math.floor(Math.random() * otherObjects.length)];
}

/**
 * Get statistics about the object bank
 */
export function getObjectBankStats() {
  const letters = getAllLetters();
  const counts = letters.map(letter => ({
    letter,
    count: getObjectsForLetter(letter).length
  }));

  return {
    totalObjects: OBJECT_BANK.length,
    totalLetters: letters.length,
    letterCounts: counts,
    minPerLetter: Math.min(...counts.map(c => c.count)),
    maxPerLetter: Math.max(...counts.map(c => c.count)),
    avgPerLetter: OBJECT_BANK.length / letters.length
  };
}
